/**
 * useGrid Hook - Manages grid state and persistence
 *
 * This hook provides comprehensive state management for the sequencer grid with automatic
 * persistence to localStorage. It handles all grid operations including cell toggling,
 * note editing, instrument selection, and composition management.
 *
 * ## Persistence Behavior
 *
 * The hook implements automatic persistence with the following characteristics:
 *
 * - **Auto-save on changes**: Compositions are automatically saved to localStorage whenever
 *   any state changes (grid, notes, instruments, tempo, etc.). The save occurs within
 *   500ms of the change due to React's batching mechanism.
 *
 * - **Auto-load on mount**: When the component mounts, any previously saved composition
 *   is automatically loaded from localStorage and restored to the grid state.
 *
 * - **Persistence across sessions**: Compositions persist across page refreshes, browser
 *   restarts, and navigation events. The composition remains saved until explicitly cleared.
 *
 * - **Clear actions persist**: When the user clears the grid (clearGrid) or resets to
 *   default (resetGrid), the cleared/reset state is automatically saved, ensuring the
 *   empty or default state persists across page refreshes.
 *
 * ## Auto-Save Mechanism
 *
 * The auto-save mechanism uses React's useEffect hook to monitor state changes:
 *
 * 1. **Trigger**: Any change to gridState, noteState, volumeState, instrumentNames,
 *    steps, or bpm triggers the save effect.
 *
 * 2. **Timing**: Saves occur synchronously but are batched by React, typically within
 *    500ms of the state change. No debouncing is needed as React batches updates.
 *
 * 3. **Load protection**: During initial mount and load, a flag (isLoadingRef) prevents
 *    the save effect from triggering, avoiding unnecessary saves during restoration.
 *
 * 4. **Error handling**: Save failures are caught and reported via the onStorageError
 *    callback, but do not interrupt the user's workflow.
 *
 * ## Error Handling Strategy
 *
 * The hook implements comprehensive error handling for storage operations:
 *
 * - **Save failures**: Caught and logged, with error details passed to onStorageError
 *   callback. The application continues functioning normally with in-memory state.
 *
 * - **Load failures**: Caught during mount, with fallback to default empty composition.
 *   Error details passed to onStorageError callback for user notification.
 *
 * - **Quota exceeded**: Detected via StorageError type and reported with specific
 *   guidance for the user to clear data.
 *
 * - **Corrupted data**: Invalid composition data triggers parse errors, falling back
 *   to default state and notifying the user.
 *
 * - **Graceful degradation**: All errors result in graceful degradation - the app
 *   continues to function with in-memory state even if persistence fails.
 *
 * @example
 * ```tsx
 * // Basic usage with auto-save enabled
 * const grid = useGrid();
 *
 * // With error handling
 * const grid = useGrid(
 *   true,
 *   (error) => {
 *     if (error.errorType === 'QUOTA_EXCEEDED') {
 *       showNotification('Storage full. Please clear some data.');
 *     } else {
 *       showNotification(`${error.type} error: ${error.message}`);
 *     }
 *   },
 *   (event) => {
 *     if (event.type === 'save') {
 *       showSaveIndicator();
 *     }
 *   }
 * );
 *
 * // Without auto-save (for testing or special cases)
 * const grid = useGrid(false);
 * ```
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { storageService, StorageError } from '../services/storage/StorageService';
import type { GridState } from '../types/grid';
import type { InstrumentType, ADSRSettings, Composition, Channel } from '../types/composition';
import type { InstrumentParameters } from '../types/instruments';
import { instrumentDatabase } from '../services/instruments/InstrumentDatabase';
import type { EffectInstance, EffectType } from '../services/effects/EffectsManager';
import { nativeAudioService } from '../services/audio/NativeAudioService';
/**
 * Default ADSR settings
 */
const DEFAULT_ADSR: ADSRSettings = {
    attack: 0.005,
    decay: 0.1,
    sustain: 0.3,
    release: 1,
};
/**
 * Default number of rows
 */
const DEFAULT_ROWS = 4;
/**
 * Default number of steps
 */
const DEFAULT_STEPS = 16;
/**
 * Default BPM
 */
const DEFAULT_BPM = 120;
/**
 * Default instrument
 */
const DEFAULT_INSTRUMENT: InstrumentType = 'square';
/**
 * Storage error callback type
 *
 * Called when a storage operation (save or load) fails. Provides error details
 * for user notification and debugging.
 *
 * @param error - Error details
 * @param error.type - Type of operation that failed ('save' or 'load')
 * @param error.message - Human-readable error message
 * @param error.errorType - Optional StorageErrorType for specific error handling
 */
export type StorageErrorCallback = (error: {
    type: 'save' | 'load';
    message: string;
    errorType?: string;
}) => void;
/**
 * Storage success callback type
 *
 * Called when a storage operation completes successfully. Can be used to show
 * save indicators or other user feedback.
 *
 * @param event - Success event details
 * @param event.type - Type of operation that succeeded (currently only 'save')
 */
export type StorageSuccessCallback = (event: {
    type: 'save';
}) => void;
/**
 * Return type for useGrid hook
 */
export interface UseGridReturn extends GridState {
    /** Array of flags indicating whether parameters are enabled for each row */
    parametersEnabled: boolean[];
    /** Array of effects chains, one per row */
    effectsChains: EffectInstance[][];
    /** Array of flags indicating whether effects panel is expanded for each row */
    effectsPanelExpanded: boolean[];
    /**
     * Toggle a cell on/off
     * Triggers auto-save when autoSave is enabled
     */
    toggleCell: (row: number, step: number) => void;
    /**
     * Set note for a cell
     * Triggers auto-save when autoSave is enabled
     */
    setNote: (row: number, step: number, note: string | null) => void;
    /**
     * Set instrument for a row
     * Triggers auto-save when autoSave is enabled
     */
    setInstrument: (row: number, instrument: InstrumentType, parameters?: InstrumentParameters) => void;
    /**
     * Update instrument parameters for a row
     * Triggers auto-save when autoSave is enabled
     */
    updateInstrumentParameters: (row: number, parameters: InstrumentParameters) => void;
    /**
     * Apply a preset to a row
     * Triggers auto-save when autoSave is enabled
     */
    applyPreset: (row: number, presetId: string) => void;
    /**
     * Get instrument parameters for a row
     * Does not trigger auto-save
     */
    getInstrumentParameters: (row: number) => InstrumentParameters | undefined;
    /**
     * Toggle parameters enabled for a row
     * Triggers auto-save when autoSave is enabled
     */
    toggleParametersEnabled: (row: number) => void;
    /**
     * Set volume for a row (0.0 to 1.0)
     * Triggers auto-save when autoSave is enabled
     */
    setVolume: (row: number, volume: number) => void;
    /**
     * Toggle mute for a row
     * Does not trigger auto-save (mute state is not persisted)
     */
    toggleMute: (row: number) => void;
    /**
     * Add a new row to the grid
     * Triggers auto-save when autoSave is enabled
     */
    addRow: () => void;
    /**
     * Remove a row from the grid
     * Triggers auto-save when autoSave is enabled
     */
    removeRow: (row: number) => void;
    /**
     * Set number of steps (minimum 4, no maximum)
     * Triggers auto-save when autoSave is enabled
     */
    setSteps: (steps: number) => void;
    /**
     * Set BPM (40-300)
     * Triggers auto-save when autoSave is enabled
     */
    setBpm: (bpm: number) => void;
    /**
     * Set current playback step
     * Does not trigger auto-save (playback position is not persisted)
     */
    setCurrentStep: (step: number) => void;
    /**
     * Clear all cells in the grid
     * Triggers auto-save, persisting the empty state
     */
    clearGrid: () => void;
    /**
     * Reset grid to default state (4 rows, 16 steps, default instruments)
     * Triggers auto-save, persisting the reset state
     */
    resetGrid: () => void;
    /**
     * Load a composition from external source
     * Triggers auto-save, persisting the loaded composition
     */
    loadComposition: (composition: Composition) => void;
    /**
     * Get current composition in serializable format
     * Does not trigger auto-save
     */
    getComposition: () => Composition;
    /**
     * Set effects chain for a row
     * Triggers auto-save when autoSave is enabled
     */
    setEffectsChain: (row: number, effects: EffectInstance[]) => void;
    /**
     * Update effect parameter for a specific effect in a row
     * Triggers auto-save when autoSave is enabled
     */
    updateEffectParameter: (row: number, effectId: string, parameterId: string, value: number) => void;
    /**
     * Toggle effect bypass state
     * Triggers auto-save when autoSave is enabled
     */
    toggleEffectBypass: (row: number, effectId: string) => void;
    /**
     * Remove effect from chain
     * Triggers auto-save when autoSave is enabled
     */
    removeEffect: (row: number, effectId: string) => void;
    /**
     * Add effect to a specific slot
     * Triggers auto-save when autoSave is enabled
     */
    addEffect: (row: number, slot: number, type: EffectType) => void;
    /**
     * Toggle effects panel expansion for a row
     * Does not trigger auto-save (UI state is not persisted)
     */
    toggleEffectsPanel: (row: number) => void;
}
/**
 * Initialize empty grid state
 */
function createEmptyGrid(rows: number, steps: number): boolean[][] {
    return Array(rows)
        .fill(null)
        .map(() => Array(steps).fill(false));
}
/**
 * Initialize empty note state
 */
function createEmptyNoteState(rows: number, steps: number): (string | null)[][] {
    return Array(rows)
        .fill(null)
        .map(() => Array(steps).fill(null));
}
/**
 * Convert grid state to composition format
 *
 * Transforms the internal grid representation (2D arrays) into the standardized
 * Composition format used for persistence and sharing.
 *
 * @param gridState - 2D array of active cells
 * @param noteState - 2D array of note pitches
 * @param volumeState - Array of volume levels per row
 * @param instrumentNames - Array of instrument types per row
 * @param instrumentParameters - Array of instrument parameters per row
 * @param effectsChains - Array of effects chains per row
 * @param steps - Number of steps in the pattern
 * @param bpm - Tempo in beats per minute
 * @returns Composition object ready for serialization
 */
function gridToComposition(gridState: boolean[][], noteState: (string | null)[][], volumeState: number[], instrumentNames: InstrumentType[], instrumentParameters: (InstrumentParameters | undefined)[], effectsChains: EffectInstance[][], steps: number, bpm: number): Composition {
    const channels = gridState.map((row, rowIndex) => {
        const notes = row
            .map((active, step) => {
            if (!active)
                return null;
            const pitch = noteState[rowIndex]?.[step] ?? 'C4';
            const volume = volumeState[rowIndex] ?? 0.8;
            return {
                step,
                pitch,
                length: 'sixteenth',
                volume,
            };
        })
            .filter((note) => note !== null) as Array<{
            step: number;
            pitch: string;
            length: string;
            volume: number;
        }>;
        const channel: Channel = {
            name: instrumentNames[rowIndex] ?? DEFAULT_INSTRUMENT,
            patternLength: steps,
            notes,
            volume: volumeState[rowIndex] ?? 0.8,
        };
        // Add parameters if they exist
        if (instrumentParameters[rowIndex]) {
            channel.parameters = instrumentParameters[rowIndex];
        }
        // Add effects chain if it exists and is not empty
        const effectsChain = effectsChains[rowIndex];
        if (effectsChain && effectsChain.length > 0) {
            channel.effectsChain = effectsChain;
        }
        return channel;
    });
    return {
        title: 'Untitled',
        tempo: bpm,
        channels,
    };
}
/**
 * Convert composition to grid state
 *
 * Transforms a Composition object (from persistence or sharing) into the internal
 * grid representation used by the hook.
 *
 * @param composition - Composition object to convert
 * @returns Object containing all grid state arrays and settings
 */
function compositionToGrid(composition: Composition): {
    gridState: boolean[][];
    noteState: (string | null)[][];
    volumeState: number[];
    instrumentNames: InstrumentType[];
    adsrSettings: ADSRSettings[];
    instrumentParameters: (InstrumentParameters | undefined)[];
    effectsChains: EffectInstance[][];
    steps: number;
    bpm: number;
} {
    const rows = composition.channels.length;
    const steps = composition.channels[0]?.patternLength ?? DEFAULT_STEPS;
    const gridState = createEmptyGrid(rows, steps);
    const noteState = createEmptyNoteState(rows, steps);
    const volumeState: number[] = [];
    const instrumentNames: InstrumentType[] = [];
    const adsrSettings: ADSRSettings[] = [];
    const instrumentParameters: (InstrumentParameters | undefined)[] = [];
    const effectsChains: EffectInstance[][] = [];
    composition.channels.forEach((channel, rowIndex) => {
        instrumentNames.push(channel.name as InstrumentType);
        // Restore channel-level volume if available, fall back to note volume or default
        if (channel.volume !== undefined && channel.volume !== null) {
            volumeState.push(channel.volume);
        } else if (channel.notes.length > 0 && channel.notes[0] && channel.notes[0].volume !== undefined) {
            // Legacy migration: use the first note's volume as channel volume
            volumeState.push(channel.notes[0].volume);
        } else {
            volumeState.push(0.8);
        }
        adsrSettings.push({ ...DEFAULT_ADSR });
        // Load parameters from channel if available, otherwise use defaults from database
        if (channel.parameters) {
            instrumentParameters.push(channel.parameters);
        }
        else {
            // Migration: apply default parameters for legacy compositions
            const metadata = instrumentDatabase.getInstrument(channel.name as InstrumentType);
            instrumentParameters.push(metadata?.defaultParameters);
        }
        // Load effects chain from channel if available (backward compatibility)
        if (channel.effectsChain) {
            effectsChains.push(channel.effectsChain as EffectInstance[]);
        }
        else {
            effectsChains.push([]);
        }
        channel.notes.forEach((note) => {
            if (note.step < steps && gridState[rowIndex] && noteState[rowIndex]) {
                gridState[rowIndex][note.step] = true;
                noteState[rowIndex][note.step] = note.pitch;
            }
        });
    });
    return {
        gridState,
        noteState,
        volumeState,
        instrumentNames,
        adsrSettings,
        instrumentParameters,
        effectsChains,
        steps,
        bpm: composition.tempo,
    };
}
/**
 * Custom hook for managing grid state
 *
 * @param autoSave - Whether to auto-save to localStorage (default: true)
 * @param onStorageError - Optional callback for storage errors
 * @param onStorageSuccess - Optional callback for successful storage operations
 * @returns Grid state and control methods
 */
export function useGrid(autoSave: boolean = true, onStorageError?: StorageErrorCallback, onStorageSuccess?: StorageSuccessCallback): UseGridReturn {
    // Track if we're currently loading to prevent save during load
    const isLoadingRef = useRef(true);
    // Initialize state
    const [gridState, setGridState] = useState<boolean[][]>(() => createEmptyGrid(DEFAULT_ROWS, DEFAULT_STEPS));
    const [noteState, setNoteState] = useState<(string | null)[][]>(() => createEmptyNoteState(DEFAULT_ROWS, DEFAULT_STEPS));
    const [volumeState, setVolumeState] = useState<number[]>(() => Array(DEFAULT_ROWS).fill(0.8));
    const [muted, setMuted] = useState<boolean[]>(() => Array(DEFAULT_ROWS).fill(false));
    const [instrumentNames, setInstrumentNames] = useState<InstrumentType[]>(() => Array(DEFAULT_ROWS).fill(DEFAULT_INSTRUMENT));
    const [adsrSettings, setAdsrSettings] = useState<ADSRSettings[]>(() => Array(DEFAULT_ROWS).fill(DEFAULT_ADSR));
    const [instrumentParameters, setInstrumentParameters] = useState<(InstrumentParameters | undefined)[]>(() => Array(DEFAULT_ROWS).fill(undefined));
    const [parametersEnabled, setParametersEnabled] = useState<boolean[]>(() => Array(DEFAULT_ROWS).fill(false));
    const [effectsChains, setEffectsChains] = useState<EffectInstance[][]>(() => Array(DEFAULT_ROWS).fill(null).map(() => []));
    const [effectsPanelExpanded, setEffectsPanelExpanded] = useState<boolean[]>(() => Array(DEFAULT_ROWS).fill(false));
    // Remembers the last pitch used per row so new cells default to it (not always C4).
    const lastPitchByRow = useRef<string[]>([]);
    const [steps, setStepsState] = useState(DEFAULT_STEPS);
    const [bpm, setBpmState] = useState(DEFAULT_BPM);
    const [currentStep, setCurrentStep] = useState(0);
    // Always-current snapshot of the per-row sound data. The cell-preview callbacks
    // are referentially stable (so memoized cells don't re-render), which means their
    // closures would otherwise capture STALE arrays — making the preview play the wrong
    // instrument. Reading from this ref guarantees the preview uses the row's CURRENT
    // instrument/params/notes as displayed.
    const liveStateRef = useRef({ instrumentNames, instrumentParameters, parametersEnabled, noteState, gridState });
    liveStateRef.current = { instrumentNames, instrumentParameters, parametersEnabled, noteState, gridState };
    /**
     * Load saved composition on mount
     *
     * This effect runs once when the component mounts and attempts to load any
     * previously saved composition from localStorage. If a composition exists,
     * it is restored to the grid state. If loading fails or no composition exists,
     * the default empty state is used.
     *
     * The isLoadingRef flag is set during this process to prevent the auto-save
     * effect from triggering during the initial load.
     *
     * Error handling:
     * - StorageError: Specific error types are passed to onStorageError callback
     * - Other errors: Generic load failure message is passed to callback
     * - All errors: Fall back to default empty composition
     */
    useEffect(() => {
        if (!autoSave) {
            isLoadingRef.current = false;
            return;
        }
        try {
            const saved = storageService.loadComposition();
            console.log('[useGrid] Loading composition from localStorage:', saved);
            if (saved) {
                const converted = compositionToGrid(saved);
                setGridState(converted.gridState);
                setNoteState(converted.noteState);
                setVolumeState(converted.volumeState);
                setInstrumentNames(converted.instrumentNames);
                setAdsrSettings(converted.adsrSettings);
                setInstrumentParameters(converted.instrumentParameters);
                setEffectsChains(converted.effectsChains);
                setStepsState(converted.steps);
                setBpmState(converted.bpm);
                setMuted(Array(converted.gridState.length).fill(false));
                setEffectsPanelExpanded(Array(converted.gridState.length).fill(false));
            }
        }
        catch (error) {
            console.error('[useGrid] Failed to load composition', error);
            // Fall back to default empty composition
            setGridState(createEmptyGrid(DEFAULT_ROWS, DEFAULT_STEPS));
            setNoteState(createEmptyNoteState(DEFAULT_ROWS, DEFAULT_STEPS));
            setVolumeState(Array(DEFAULT_ROWS).fill(0.8));
            setMuted(Array(DEFAULT_ROWS).fill(false));
            setInstrumentNames(Array(DEFAULT_ROWS).fill(DEFAULT_INSTRUMENT));
            setAdsrSettings(Array(DEFAULT_ROWS).fill(DEFAULT_ADSR));
            setInstrumentParameters(Array(DEFAULT_ROWS).fill(undefined));
            setEffectsChains(Array(DEFAULT_ROWS).fill(null).map(() => []));
            setEffectsPanelExpanded(Array(DEFAULT_ROWS).fill(false));
            setStepsState(DEFAULT_STEPS);
            setBpmState(DEFAULT_BPM);
            // Notify parent component of load error
            if (onStorageError) {
                if (error instanceof StorageError) {
                    onStorageError({
                        type: 'load',
                        message: error.message,
                        errorType: error.type,
                    });
                }
                else {
                    onStorageError({
                        type: 'load',
                        message: 'Failed to load composition',
                    });
                }
            }
        }
        finally {
            // Mark loading as complete after a brief delay to ensure all state updates have been applied
            setTimeout(() => {
                isLoadingRef.current = false;
            }, 0);
        }
    }, [autoSave, onStorageError]);
    /**
     * Save composition when state changes (auto-save effect)
     *
     * This effect monitors all composition-related state and automatically saves
     * to localStorage whenever any state changes. This ensures that user work is
     * continuously protected without requiring manual save actions.
     *
     * Timing:
     * - Triggers on any change to: gridState, noteState, volumeState, instrumentNames,
     *   steps, or bpm
     * - React batches state updates, so multiple rapid changes result in a single save
     * - Typical save occurs within 500ms of the last state change
     *
     * Protection:
     * - Does not run during initial mount/load (isLoadingRef prevents this)
     * - Only runs when autoSave is enabled
     *
     * Error handling:
     * - Save failures are caught and logged
     * - Errors are passed to onStorageError callback for user notification
     * - Application continues functioning with in-memory state on failure
     *
     * Success notification:
     * - Successful saves trigger onStorageSuccess callback
     * - Can be used to show "Saved" indicators to users
     */
    useEffect(() => {
        if (!autoSave)
            return;
        // Don't save during initial load
        if (isLoadingRef.current) {
            return;
        }
        try {
            const composition = gridToComposition(gridState, noteState, volumeState, instrumentNames, instrumentParameters, effectsChains, steps, bpm);
            console.log('[useGrid] Saving composition to localStorage:', composition);
            storageService.saveComposition(composition);
            // Notify parent component of successful save
            if (onStorageSuccess) {
                onStorageSuccess({ type: 'save' });
            }
        }
        catch (error) {
            console.error('[useGrid] Failed to save composition', error);
            // Notify parent component of save error
            if (onStorageError) {
                if (error instanceof StorageError) {
                    onStorageError({
                        type: 'save',
                        message: error.message,
                        errorType: error.type,
                    });
                }
                else {
                    onStorageError({
                        type: 'save',
                        message: 'Failed to save composition',
                    });
                }
            }
        }
    }, [gridState, noteState, volumeState, instrumentNames, instrumentParameters, effectsChains, steps, bpm, autoSave, onStorageError, onStorageSuccess]);
    /**
     * Sync effects chains with NativeAudioService
     *
     * This effect monitors effectsChains state and updates the NativeAudioService
     * EffectsManager whenever effects change. This ensures that audio processing
     * is updated in real-time as users modify effects.
     *
     * The effect runs asynchronously to avoid blocking the UI thread during
     * worklet node creation and effects chain updates.
     */
    useEffect(() => {
        // Don't sync during initial load
        if (isLoadingRef.current) {
            return;
        }
        // Sync each row's effects chain with NativeAudioService
        effectsChains.forEach((chain, row) => {
            // Update effects chain asynchronously
            nativeAudioService.updateEffectsChain(row, chain).catch((error) => {
                console.error(`[useGrid] Failed to update effects chain for row ${row}:`, error);
            });
        });
    }, [effectsChains]);
    /**
     * Toggle cell on/off
     *
     * Toggles the active state of a cell in the grid. When a cell is turned off,
     * its associated note is cleared. When turned on, a default note (C4) is set
     * if no note exists.
     *
     * Persistence: Triggers auto-save effect when autoSave is enabled
     *
     * @param row - Row index (0-based)
     * @param step - Step index (0-based)
     */
    const toggleCell = useCallback((row: number, step: number) => {
        // Read CURRENT per-row data from the ref (this callback is stable, so its
        // closure can't be trusted to hold the latest arrays).
        const live = liveStateRef.current;
        // New cells default to the last pitch used in this row (not always C4).
        const defaultPitch = lastPitchByRow.current[row] ?? 'C4';
        // Audible feedback: preview the note in the row's CURRENT sound when toggling ON.
        // adsr=undefined mirrors playback (the voice uses the instrument's own envelope
        // from its parameters), and reading from `live` guarantees the right instrument.
        if (!live.gridState[row]?.[step]) {
            const instrument = live.instrumentNames[row];
            if (instrument) void nativeAudioService.previewNote(instrument, live.noteState[row]?.[step] ?? defaultPitch, live.instrumentParameters[row], undefined, live.parametersEnabled[row]);
        }
        setGridState((prev) => {
            const next = prev.map((r) => [...r]);
            const rowData = next[row];
            if (!rowData)
                return prev;
            const currentValue = rowData[step];
            if (currentValue === undefined)
                return prev;
            rowData[step] = !currentValue;
            // Clear note if cell is turned off
            if (!rowData[step]) {
                setNoteState((prevNotes) => {
                    const nextNotes = prevNotes.map((r) => [...r]);
                    const noteRow = nextNotes[row];
                    if (noteRow) {
                        noteRow[step] = null;
                    }
                    return nextNotes;
                });
            }
            else if (!live.noteState[row]?.[step]) {
                // Set default note if cell is turned on and no note exists
                setNoteState((prevNotes) => {
                    const nextNotes = prevNotes.map((r) => [...r]);
                    const noteRow = nextNotes[row];
                    if (noteRow) {
                        noteRow[step] = defaultPitch;
                    }
                    return nextNotes;
                });
            }
            return next;
        });
    }, []);
    /**
     * Set note for a cell
     *
     * Sets the pitch value for a specific cell. If a note is set, the cell is
     * automatically activated. Setting null clears the note but keeps the cell active.
     *
     * Persistence: Triggers auto-save effect when autoSave is enabled
     *
     * @param row - Row index (0-based)
     * @param step - Step index (0-based)
     * @param note - Note pitch (e.g., 'C4', 'D#5') or null to clear
     */
    const setNote = useCallback((row: number, step: number, note: string | null) => {
        // Remember the last pitch used in this row (so new cells default to it).
        if (note) {
            lastPitchByRow.current[row] = note;
            // Audible feedback: preview the exact pitch in the row's CURRENT sound.
            // Read from liveStateRef (stable callback ⇒ closure may be stale) and pass
            // adsr=undefined to mirror playback (voice uses the instrument's own envelope).
            const live = liveStateRef.current;
            const instrument = live.instrumentNames[row];
            if (instrument) void nativeAudioService.previewNote(instrument, note, live.instrumentParameters[row], undefined, live.parametersEnabled[row]);
        }
        setNoteState((prev) => {
            const next = prev.map((r) => [...r]);
            if (next[row]) {
                next[row][step] = note;
            }
            return next;
        });
        // Ensure cell is active if setting a note
        if (note) {
            setGridState((prev) => {
                const next = prev.map((r) => [...r]);
                if (next[row]) {
                    next[row][step] = true;
                }
                return next;
            });
        }
    }, []);
    /**
     * Set instrument
     */
    const setInstrument = useCallback((row: number, instrument: InstrumentType, parameters?: InstrumentParameters) => {
        setInstrumentNames((prev) => {
            const next = [...prev];
            next[row] = instrument;
            return next;
        });
        // If parameters are provided, use them; otherwise use defaults from database
        if (parameters) {
            setInstrumentParameters((prev) => {
                const next = [...prev];
                next[row] = parameters;
                return next;
            });
        }
        else {
            // Load default parameters for the instrument
            const metadata = instrumentDatabase.getInstrument(instrument);
            setInstrumentParameters((prev) => {
                const next = [...prev];
                next[row] = metadata?.defaultParameters;
                return next;
            });
        }
    }, []);
    /**
     * Update instrument parameters for a row
     */
    const updateInstrumentParameters = useCallback((row: number, parameters: InstrumentParameters) => {
        setInstrumentParameters((prev) => {
            const next = [...prev];
            const existing = next[row] || {};
            // Merge with existing parameters, only including defined properties
            const merged: InstrumentParameters = {};
            if (parameters.adsr || existing.adsr) {
                // Ensure all ADSR properties are defined
                const baseAdsr = existing.adsr || DEFAULT_ADSR;
                merged.adsr = {
                    attack: parameters.adsr?.attack ?? baseAdsr.attack,
                    decay: parameters.adsr?.decay ?? baseAdsr.decay,
                    sustain: parameters.adsr?.sustain ?? baseAdsr.sustain,
                    release: parameters.adsr?.release ?? baseAdsr.release,
                };
            }
            if (parameters.oscillator || existing.oscillator) {
                merged.oscillator = { ...existing.oscillator, ...parameters.oscillator };
            }
            if (parameters.filter || existing.filter) {
                merged.filter = { ...existing.filter, ...parameters.filter };
            }
            if (parameters.effects || existing.effects) {
                merged.effects = { ...existing.effects, ...parameters.effects };
            }
            // pitchAutomation (e.g. vibrato) is authoritative from the editor: present
            // means on, absent means off. Don't fall back to existing, or it could
            // never be toggled back off. (This field was previously dropped here, so
            // the vibrato checkbox never stayed checked.)
            if (parameters.pitchAutomation) {
                merged.pitchAutomation = parameters.pitchAutomation;
            }
            if (parameters.dacQuantization !== undefined) {
                merged.dacQuantization = parameters.dacQuantization;
            }
            // drumTuning is authoritative from the editor too — the percussion
            // generators regenerate their buffers from it. Carried via the
            // editor's currentParams, so present means "use these tunings".
            if (parameters.drumTuning) {
                merged.drumTuning = parameters.drumTuning;
            }
            next[row] = merged;
            return next;
        });
    }, []);
    /**
     * Apply a preset to a row
     */
    const applyPreset = useCallback((row: number, presetId: string) => {
        const instrument = instrumentNames[row];
        if (!instrument)
            return;
        const metadata = instrumentDatabase.getInstrument(instrument);
        const preset = metadata?.presets.find(p => p.id === presetId);
        if (preset) {
            setInstrumentParameters((prev) => {
                const next = [...prev];
                next[row] = preset.parameters;
                return next;
            });
        }
    }, [instrumentNames]);
    /**
     * Get instrument parameters for a row
     */
    const getInstrumentParameters = useCallback((row: number): InstrumentParameters | undefined => {
        return instrumentParameters[row];
    }, [instrumentParameters]);
    /**
     * Set volume
     */
    const setVolume = useCallback((row: number, volume: number) => {
        setVolumeState((prev) => {
            const next = [...prev];
            next[row] = Math.max(0, Math.min(1, volume));
            return next;
        });
    }, []);
    /**
     * Toggle mute
     */
    const toggleMute = useCallback((row: number) => {
        setMuted((prev) => {
            const next = [...prev];
            next[row] = !next[row];
            return next;
        });
    }, []);
    /**
     * Add row
     */
    const addRow = useCallback(() => {
        setGridState((prev) => [...prev, Array(steps).fill(false)]);
        setNoteState((prev) => [...prev, Array(steps).fill(null)]);
        setVolumeState((prev) => [...prev, 0.8]);
        setMuted((prev) => [...prev, false]);
        setInstrumentNames((prev) => [...prev, DEFAULT_INSTRUMENT]);
        setAdsrSettings((prev) => [...prev, { ...DEFAULT_ADSR }]);
        setParametersEnabled((prev) => [...prev, false]);
        setEffectsChains((prev) => [...prev, []]);
        setEffectsPanelExpanded((prev) => [...prev, false]);
        // Add default parameters for the default instrument
        const metadata = instrumentDatabase.getInstrument(DEFAULT_INSTRUMENT);
        setInstrumentParameters((prev) => [...prev, metadata?.defaultParameters]);
    }, [steps]);
    /**
     * Remove row
     */
    const removeRow = useCallback((row: number) => {
        if (gridState.length <= 1)
            return; // Keep at least one row
        setGridState((prev) => prev.filter((_, i) => i !== row));
        setNoteState((prev) => prev.filter((_, i) => i !== row));
        setVolumeState((prev) => prev.filter((_, i) => i !== row));
        setMuted((prev) => prev.filter((_, i) => i !== row));
        setInstrumentNames((prev) => prev.filter((_, i) => i !== row));
        setAdsrSettings((prev) => prev.filter((_, i) => i !== row));
        setInstrumentParameters((prev) => prev.filter((_, i) => i !== row));
        setParametersEnabled((prev) => prev.filter((_, i) => i !== row));
        setEffectsChains((prev) => prev.filter((_, i) => i !== row));
        setEffectsPanelExpanded((prev) => prev.filter((_, i) => i !== row));
    }, [gridState.length]);
    /**
     * Set steps
     */
    const setSteps = useCallback((newSteps: number) => {
        // Allow minimum of 4 steps, no maximum limit
        const validSteps = Math.max(4, newSteps);
        setStepsState(validSteps);
        // Adjust grid and note state
        setGridState((prev) => prev.map((row) => {
            if (row.length < validSteps) {
                return [...row, ...Array(validSteps - row.length).fill(false)];
            }
            return row.slice(0, validSteps);
        }));
        setNoteState((prev) => prev.map((row) => {
            if (row.length < validSteps) {
                return [...row, ...Array(validSteps - row.length).fill(null)];
            }
            return row.slice(0, validSteps);
        }));
    }, []);
    /**
     * Set BPM
     */
    const setBpm = useCallback((newBpm: number) => {
        const validBpm = Math.max(40, Math.min(300, newBpm));
        setBpmState(validBpm);
    }, []);
    /**
     * Clear all cells in the grid
     *
     * Removes all active cells and notes from the grid while preserving the current
     * number of rows and steps. This is a destructive action that should be confirmed
     * by the user before calling.
     *
     * Persistence: Triggers auto-save effect, persisting the empty state. After a
     * page refresh, the grid will remain empty until the user adds new notes.
     */
    const clearGrid = useCallback(() => {
        const rows = gridState.length;
        setGridState(createEmptyGrid(rows, steps));
        setNoteState(createEmptyNoteState(rows, steps));
    }, [gridState.length, steps]);
    /**
     * Reset grid to default state
     *
     * Resets the entire grid to the default initial state: 4 rows, 16 steps,
     * default instruments (square wave), default volume (0.8), and default tempo (120 BPM).
     * This is a destructive action that should be confirmed by the user before calling.
     *
     * Persistence: Triggers auto-save effect, persisting the reset state. After a
     * page refresh, the grid will be in the default state until the user makes changes.
     */
    const resetGrid = useCallback(() => {
        setGridState(createEmptyGrid(DEFAULT_ROWS, DEFAULT_STEPS));
        setNoteState(createEmptyNoteState(DEFAULT_ROWS, DEFAULT_STEPS));
        setVolumeState(Array(DEFAULT_ROWS).fill(0.8));
        setMuted(Array(DEFAULT_ROWS).fill(false));
        setInstrumentNames(Array(DEFAULT_ROWS).fill(DEFAULT_INSTRUMENT));
        setAdsrSettings(Array(DEFAULT_ROWS).fill(DEFAULT_ADSR));
        setInstrumentParameters(Array(DEFAULT_ROWS).fill(undefined));
        setParametersEnabled(Array(DEFAULT_ROWS).fill(false));
        setEffectsChains(Array(DEFAULT_ROWS).fill(null).map(() => []));
        setEffectsPanelExpanded(Array(DEFAULT_ROWS).fill(false));
        setStepsState(DEFAULT_STEPS);
        setBpmState(DEFAULT_BPM);
        setCurrentStep(0);
    }, []);
    /**
     * Load composition from external source
     *
     * Loads a composition from an external source (e.g., shared link, template, file).
     * Replaces all current grid state with the loaded composition data.
     *
     * Persistence: Triggers auto-save effect, persisting the loaded composition.
     * The loaded composition will be restored on page refresh.
     *
     * @param composition - Composition object to load
     */
    const loadComposition = useCallback((composition: Composition) => {
        const converted = compositionToGrid(composition);
        setGridState(converted.gridState);
        setNoteState(converted.noteState);
        setVolumeState(converted.volumeState);
        setInstrumentNames(converted.instrumentNames);
        setAdsrSettings(converted.adsrSettings);
        setInstrumentParameters(converted.instrumentParameters);
        setEffectsChains(converted.effectsChains);
        setParametersEnabled(Array(converted.gridState.length).fill(false));
        setEffectsPanelExpanded(Array(converted.gridState.length).fill(false));
        setStepsState(converted.steps);
        setBpmState(converted.bpm);
        setMuted(Array(converted.gridState.length).fill(false));
        setCurrentStep(0);
    }, []);
    /**
     * Toggle parameters enabled for a row
     */
    const toggleParametersEnabled = useCallback((row: number) => {
        setParametersEnabled((prev) => {
            const next = [...prev];
            next[row] = !next[row];
            return next;
        });
    }, []);
    /**
     * Set effects chain for a row
     */
    const setEffectsChain = useCallback((row: number, effects: EffectInstance[]) => {
        setEffectsChains((prev) => {
            const next = [...prev];
            next[row] = effects;
            return next;
        });
    }, []);
    /**
     * Update effect parameter for a specific effect in a row
     */
    const updateEffectParameter = useCallback((row: number, effectId: string, parameterId: string, value: number) => {
        setEffectsChains((prev) => {
            const next = prev.map((chain) => [...chain]);
            const chain = next[row];
            if (!chain)
                return prev;
            const effectIndex = chain.findIndex((effect) => effect.id === effectId);
            if (effectIndex === -1)
                return prev;
            const effect = chain[effectIndex];
            if (!effect)
                return prev;
            chain[effectIndex] = {
                ...effect,
                parameters: {
                    ...effect.parameters,
                    [parameterId]: value,
                },
            };
            return next;
        });
    }, []);
    /**
     * Toggle effect bypass state
     */
    const toggleEffectBypass = useCallback((row: number, effectId: string) => {
        setEffectsChains((prev) => {
            const next = prev.map((chain) => [...chain]);
            const chain = next[row];
            if (!chain)
                return prev;
            const effectIndex = chain.findIndex((effect) => effect.id === effectId);
            if (effectIndex === -1)
                return prev;
            const effect = chain[effectIndex];
            if (!effect)
                return prev;
            chain[effectIndex] = {
                ...effect,
                bypassed: !effect.bypassed,
            };
            return next;
        });
    }, []);
    /**
     * Remove effect from chain
     */
    const removeEffect = useCallback((row: number, effectId: string) => {
        setEffectsChains((prev) => {
            const next = [...prev];
            const chain = next[row];
            if (!chain)
                return prev;
            next[row] = chain.filter((effect) => effect.id !== effectId);
            return next;
        });
    }, []);
    /**
     * Add effect to a specific slot
     */
    const addEffect = useCallback((row: number, slot: number, type: EffectType) => {
        setEffectsChains((prev) => {
            const next = [...prev];
            const chain = next[row];
            if (!chain)
                return prev;
            // Generate unique ID for the effect
            const id = `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            // Get default parameters for the effect type
            const defaultParameters: Record<string, number> = {};
            if (type === 'bitcrusher') {
                defaultParameters['sampleRate'] = 8000;
                defaultParameters['bitDepth'] = 4;
            }
            const newEffect: EffectInstance = {
                id,
                type,
                parameters: defaultParameters,
                bypassed: false,
                slot,
            };
            // Add effect to chain
            next[row] = [...chain, newEffect];
            return next;
        });
    }, []);
    /**
     * Toggle effects panel expansion for a row
     */
    const toggleEffectsPanel = useCallback((row: number) => {
        setEffectsPanelExpanded((prev) => {
            const next = [...prev];
            next[row] = !next[row];
            return next;
        });
    }, []);
    /**
     * Get current composition
     */
    const getComposition = useCallback((): Composition => {
        return gridToComposition(gridState, noteState, volumeState, instrumentNames, instrumentParameters, effectsChains, steps, bpm);
    }, [gridState, noteState, volumeState, instrumentNames, instrumentParameters, effectsChains, steps, bpm]);
    return {
        gridState,
        noteState,
        volumeState,
        muted,
        instrumentNames,
        adsrSettings,
        parametersEnabled,
        effectsChains,
        effectsPanelExpanded,
        steps,
        bpm,
        currentStep,
        toggleCell,
        setNote,
        setInstrument,
        updateInstrumentParameters,
        applyPreset,
        getInstrumentParameters,
        toggleParametersEnabled,
        setVolume,
        toggleMute,
        addRow,
        removeRow,
        setSteps,
        setBpm,
        setCurrentStep,
        clearGrid,
        resetGrid,
        loadComposition,
        getComposition,
        setEffectsChain,
        updateEffectParameter,
        toggleEffectBypass,
        removeEffect,
        addEffect,
        toggleEffectsPanel,
    };
}
